import { CommonModule } from '@angular/common';
import {
  Component, EventEmitter, inject, Input, OnChanges, OnDestroy, OnInit, Output, SimpleChanges,
} from '@angular/core';
import {
  AbstractControl,
  FormControl,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
  Validators,
  ControlContainer,
} from '@angular/forms';
import { Subscription } from 'rxjs';

import { AppUtilityService } from 'src/app/app-utility.service';
import { BranchEmployerService } from 'src/app/core/services/branch-employer.service';
import {
  BasicEmployersSearchComponent,
} from 'src/app/features/branch-employers/basic-employer-form/basic-employer-form.component';
import { EmployerDetailsModel } from 'src/app/features/branch-employers/models/employer-details-model';
import { UpdateEmployerComponent } from 'src/app/features/branch-employers/update-employer/update-employer.component';
import { NumbersOnlyDirective, UniquePayslip } from 'src/app/shared/directives';
import { LMSDialogHandlerService } from 'src/app/shared/lmsdialog-handler.service';
import { MdbDatepickerModule, MdbModalService, MdbSelectModule } from 'src/lib/mdb5-adapted/public-api';

import { LMSConstantsService } from '@lmsConstants';
import { PaySlipModel1, PreAuthorisationModel } from '@lmsModels';
import { ZARcurrencyPipe } from '@lmsSharedUI';

const PERMANENTDESC = 'Permanent';

@Component({
  selector: 'app-salaried-editor',
  templateUrl: './salaried-editor.component.html',
  styleUrls: [ './salaried-editor.component.scss' ],
  standalone: true,
  // encapsulation: ViewEncapsulation.Emulated,
  viewProviders: [
    {
      provide: ControlContainer,
      useFactory: () => inject(ControlContainer, { skipSelf: true }),
    },
  ],
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    MdbSelectModule,
    MdbDatepickerModule,
    BasicEmployersSearchComponent,
    ZARcurrencyPipe,
    NumbersOnlyDirective
  ],
})
export class SalariedEditorComponent implements OnInit, OnDestroy, OnChanges {
  @Input() controlKey = '';
  parentContainer = inject(ControlContainer);
  @Input() sallabel?: string;
  @Input() Submitted = false;
  @Input() preauthModel?: PreAuthorisationModel;
  @Output() paySlipCaptured = new EventEmitter<PaySlipModel1>();
  private readonly lmsConstantsService = inject(LMSConstantsService);
  employerDetailsModel?: EmployerDetailsModel;

  updateEmployment = false;
  employmentTypes = this.lmsConstantsService.getLMSConstant('EMPLOYMENTTYPE') || [];
  wageFrequencies = this.lmsConstantsService.getLMSConstant('WAGEFREQUENCY') || [];
  payDates = this.lmsConstantsService.getLMSConstant('PAYDATES') || [];
  payDaysOfWeek = this.lmsConstantsService.getLMSConstant('PAYDAYOFWEEK') || [];
  industryTypes = this.lmsConstantsService.getLMSConstant('INDUSTRYTYPE') || [];
  lstAdditionalIncome = [
    { value: 'None', description: 'None' },
    { value: 'Rentals', description: 'Rentals' },
    { value: 'Maintanence', description: 'Maintanence' },
    { value: 'Bonus', description: 'Bonus' },
    { value: 'Dividends', description: 'Dividends' },
    { value: 'Royalties', description: 'Royalties' },
    { value: 'Other', description: 'Other' },
  ];
  originalEmployer: string;
  maxDate = new Date();
  startDate = new Date(2010, 0, 1);
  employerNametxt = '';
  payDateError = '';
  invalidDate = false;
  invalidEndDate = false;
  payslipResult: string;
  showPayslip: any;

  // Income storage for different wage frequencies
  income1 = 0;
  income2 = 0;
  income3 = 0;

  // Income arrays for Weekly and Fortnightly (using correct value from constants)
  weeklyIncomes: number[] = [0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0];
  fortnightlyIncomes: number[] = [0, 0, 0, 0, 0, 0];

  // Store weekly and fortnightly payslips in separate arrays
  weeklyPayslips: PaySlipModel1[] = [];
  fortnightlyPayslips: PaySlipModel1[] = [];
  private additionalIncomeSub?: Subscription;
  private employmentTypeSub?: Subscription;
  private readonly fallbackControl = new FormControl(null);
  private readonly fallbackGroup = new FormGroup({});

  isAINoneSelected = false;
  isAIOtherOptionSelected = false;

  get showContractOrTemporary () {
    const ctype = this.f.employmentType.value ?? PERMANENTDESC;
    return ctype && ctype !== PERMANENTDESC;
  }

  get parentFormGroup () {
    return this.parentContainer.control as FormGroup;
  }

  get f () {
    return (this.parentFormGroup?.get(this.controlKey) as FormGroup)?.controls;
  }

  get totalIncome () {
    const wageFreq = this.f.wageFrequency.value;

    if (wageFreq === 'Weekly') {
      return this.weeklyIncomes.reduce((sum, income) => sum + income, 0);
    } else if (wageFreq === 'Fortnightly') {
      return this.fortnightlyIncomes.reduce((sum, income) => sum + income, 0);
    } else {
      // Monthly
      return +this.income1 + +this.income2 + +this.income3;
    }
  }

  get isWeeklyClient() {
    return this.f.wageFrequency.value === 'Weekly';
  }

  get isFortnightlyClient() {
    return this.f.wageFrequency.value === 'Fortnightly';
  }

  get isMonthlyClient() {
    return this.f.wageFrequency.value === 'Monthly';
  }

  getWeeklyMonthTotal(month: number): number {
    const monthIndex = month - 1;
    const start = monthIndex * 4;

    return this.weeklyIncomes.slice(start, start + 4).reduce((sum, income) => sum + income, 0);
  }

  get isMonth1WeeklyComplete(): boolean {
    return this.weeklyIncomes.slice(0, 4).every((income) => income > 0);
  }

  get isMonth2WeeklyComplete(): boolean {
    return this.weeklyIncomes.slice(4, 8).every((income) => income > 0);
  }

  get isMonth3WeeklyComplete(): boolean {
    return this.weeklyIncomes.slice(8, 12).every((income) => income > 0);
  }

  getFortnightlyMonthTotal(month: number): number {
    const monthIndex = month - 1;
    const start = monthIndex * 2;

    return this.fortnightlyIncomes.slice(start, start + 2).reduce((sum, income) => sum + income, 0);
  }

  get isMonth1FortnightlyComplete(): boolean {
    return this.fortnightlyIncomes.slice(0, 2).every((income) => income > 0);
  }

  get isMonth2FortnightlyComplete(): boolean {
    return this.fortnightlyIncomes.slice(2, 4).every((income) => income > 0);
  }

  get isMonth3FortnightlyComplete(): boolean {
    return this.fortnightlyIncomes.slice(4, 6).every((income) => income > 0);
  }

  get payDateOptions() {
    return this.f?.wageFrequency?.value === 'Monthly' ? this.payDates : this.payDaysOfWeek;
  }

  constructor (
    private branchEmployerService: BranchEmployerService,
    public lmsDialogHandler: LMSDialogHandlerService,
    public global_utility: AppUtilityService,
    public modalService: MdbModalService
  ) {}

  get employmentStartDate () {
    return this.f?.employmentStartDate || this.fallbackControl;
  }

  get contractStartDate () {
    return this.f?.contractStartDate || this.fallbackControl;
  }

  get contractEndDate () {
    return this.f?.contractEndDate || this.fallbackControl;
  }

  get employmentType () {
    return this.f?.employmentType || this.fallbackControl;
  }

  get wageFrequency () {
    return this.f?.wageFrequency || this.fallbackControl;
  }

  get payDate () {
    return this.f?.payDate || this.fallbackControl;
  }

  get workSaturday () {
    return this.f?.workSaturday || this.fallbackControl;
  }

  get employer () {
    return this.f?.employer || this.fallbackControl;
  }

  get payslips () {
    return (this.f?.payslips as FormGroup) || this.fallbackGroup;
  }

  get payslipControls () {
    const controls = this.payslips?.controls as { [key: string]: AbstractControl<any, any> } | undefined;
    return {
      payslip1: controls?.payslip1 || this.fallbackControl,
      payslip2: controls?.payslip2 || this.fallbackControl,
      payslip3: controls?.payslip3 || this.fallbackControl,
    };
  }

  ngOnInit () {
    this.parentFormGroup.setControl(
      this.controlKey,
      new FormGroup(
        {
          industryType: new FormControl(null, Validators.required),
          industryTypeOther: new FormControl(''),
          employmentType: new FormControl(this.employmentTypes[0]?.value || '', Validators.required),
          employmentStartDate: new FormControl(null),
          contractStartDate: new FormControl(null),
          contractEndDate: new FormControl(null),
          employer: new FormControl(''),
          employerTNR: new FormControl(''),
          wageFrequency: new FormControl('Monthly'),
          payDate: new FormControl(''),
          occupation: new FormControl(''),
          workSaturday: new FormControl(false),
          additionalIncome: new FormControl(null, Validators.required),
          additionalIncomeOther: new FormControl(''),
          payslips: new FormGroup(
            {
              payslip1: new FormControl({}),
              payslip2: new FormControl({}),
              payslip3: new FormControl({}),
            },
            { validators: UniquePayslip }
          ),
        },
        { updateOn: 'change' }
      ),
      { emitEvent: false }
    );

    this.additionalIncomeSub = this.f.additionalIncome?.valueChanges.subscribe((value) => {
      this.onAdditionalIncomeSelectionChange(value);
    });

    this.employmentTypeSub = this.f.employmentType?.valueChanges.subscribe(() => {
      this.toggleEmploymentType();
    });

    this.toggleEmploymentType();
  }

  ngOnChanges (changes: SimpleChanges): void {
    if (!(changes.preauthModel?.firstChange ?? false) && Object.keys(changes.preauthModel?.currentValue ?? []).length > 0) {
      setTimeout(() => {
        this.preauthModel = changes.preauthModel.currentValue;
        this.updateProfile(changes.preauthModel.currentValue);
      });
    }
  }

  updateProfile (preAuth: PreAuthorisationModel) {
    if (!preAuth || Object.values(preAuth).length == 0) {
      return;
    }

    const toDateOrDefault = (value: any, fallback: Date | null) => {
      if (!value) {
        return fallback;
      }

      const date = new Date(value);
      return Number.isNaN(date.getTime()) ? fallback : date;
    };

    const employerDetailsModel = preAuth.employment && preAuth.employment.id > 0 ? preAuth.employment : preAuth;
    this.employerDetailsModel = employerDetailsModel as EmployerDetailsModel;

    if (!employerDetailsModel.employmentType || employerDetailsModel.employmentType == 'SASSA') {
      employerDetailsModel.employmentType = PERMANENTDESC;
      employerDetailsModel.employerTNR = 0;
      employerDetailsModel.wageFrequency = 'Monthly';
      employerDetailsModel.workSaturday = false;
      employerDetailsModel.industryType = '';
      employerDetailsModel.occupation = '';
      employerDetailsModel.paydate = 4;
    }

    console.log('preAuth', preAuth);
    this.originalEmployer = preAuth.employer || preAuth.employment?.name || preAuth.employerName || '';

    this.updateEmployment = !this.originalEmployer || this.originalEmployer == 'SASSA';

    this.employerNametxt = this.originalEmployer
      ? `${this.originalEmployer},${employerDetailsModel.employerTNR || '0'}`
      : '';
    this.employer.setValue(this.employerNametxt, {
      onlySelf: false,
      emitEvent: false,
    });
    //this.employer.updateValueAndValidity({ onlySelf: false, emitEvent: false });

    this.f.employerTNR.setValue(employerDetailsModel.employerTNR || 0, {
      onlySelf: false,
      emitEvent: false,
    });
    this.f.employmentType.setValue(employerDetailsModel.employmentType || PERMANENTDESC, {
      onlySelf: true,
      emitEvent: true,
    });

    if (employerDetailsModel.employmentType == PERMANENTDESC) {
      this.f.employmentStartDate.setValue(toDateOrDefault(employerDetailsModel.employmentStartDate, null), {
        onlySelf: false,
      });
    } else {
      this.f.contractStartDate.setValue(toDateOrDefault(employerDetailsModel.contractStartDate, null), {
        onlySelf: false,
      });
      this.f.contractEndDate.setValue(toDateOrDefault(employerDetailsModel.contractEndDate, null), {
        onlySelf: false,
      });
    }

    this.f.wageFrequency.setValue(
      employerDetailsModel.wageFrequency || employerDetailsModel.wageCalendar || 'Monthly',
      {
        onlySelf: false,
        emitEvent: true,
      }
    );
    this.f.workSaturday.setValue(employerDetailsModel.workSaturday, {
      onlySelf: true,
      emitEvent: false,
    });
    this.f.occupation.setValue(employerDetailsModel.occupation, {
      onlySelf: true,
      emitEvent: false,
    });

    const industryTypeRaw = `${employerDetailsModel.industryType ?? preAuth.industryType ?? ''}`.trim();
    const industryTypeIsOther = industryTypeRaw.toLowerCase().includes('other');
    const industryTypeValue = industryTypeIsOther ? 'Other' : (industryTypeRaw || null);
    const industryTypeOtherFromValue = industryTypeIsOther
      ? industryTypeRaw.split(':').slice(1).join(':').trim()
      : '';

    this.f.industryType.setValue(industryTypeValue, {
      onlySelf: true,
      emitEvent: false,
    });
    this.f.industryTypeOther.setValue(preAuth.industryTypeOther || industryTypeOtherFromValue || '', {
      onlySelf: true,
      emitEvent: false,
    });

    // Load income data based on wage frequency
    this.loadIncomeData(preAuth);

    this.toggleEmploymentType();

    const additionalIncomeRaw = preAuth.additionalIncome?.length == 0 ? [] : preAuth.additionalIncome;
    const additionalIncomeValue = this.fromAttribute(additionalIncomeRaw);
    const additionalIncomeArray = (Array.isArray(additionalIncomeValue)
      ? additionalIncomeValue
      : additionalIncomeValue
      ? [additionalIncomeValue]
      : [])
      .map((item: any) => `${item ?? ''}`.trim())
      .filter((item: string) => item.length > 0)
      .map((item: string) => item.toLowerCase().includes('other') ? 'Other' : item);

    const additionalIncomeSelected = additionalIncomeArray.length > 0
      ? additionalIncomeArray[0]
      : null;

    const additionalIncomeOtherFromSelection = (Array.isArray(additionalIncomeValue)
      ? additionalIncomeValue
      : additionalIncomeValue
      ? [additionalIncomeValue]
      : [])
      .map((item: any) => `${item ?? ''}`.trim())
      .find((item: string) => item.toLowerCase().includes('other'))
      ?.split(':')
      .slice(1)
      .join(':')
      .trim();

    this.f.additionalIncome?.patchValue(additionalIncomeSelected, {
      onlySelf: true,
      emitEvent: false,
    });
    this.onAdditionalIncomeSelectionChange(additionalIncomeSelected);

    this.f.additionalIncomeOther.setValue(preAuth.additionalIncomeOther || additionalIncomeOtherFromSelection || '', {
      onlySelf: true,
      emitEvent: false,
    });

    this.paydaySelect((this.employerDetailsModel?.payDate || preAuth.payDate || '4').toString());
  }

  loadIncomeData(preAuth: PreAuthorisationModel) {
    const wageFreq = this.f.wageFrequency.value || 'Monthly';

    if (wageFreq === 'Weekly') {
      // Try to load from existing payslips or calculate from monthly data
      this.loadOrCalculateWeeklyIncomes(preAuth);
    } else if (wageFreq === 'Fortnightly') {
      // Try to load from existing payslips or calculate from monthly data
      this.loadOrCalculateFortnightlyIncomes(preAuth);
    } else {
      // Monthly - load existing payslips
      if (preAuth.payslips && preAuth.payslips.length > 0) {
        preAuth.payslip1 ||= preAuth.payslips[0] ?? null;
        if (preAuth.payslip1 && preAuth.payslip1.payslipDate) {
          preAuth.payslip1.date = preAuth.payslip1.payslipDate;
          preAuth.payslip1.month = 1;
          this.income1 = +preAuth.payslip1.nettIncome + +preAuth.payslip1.otherIncome;
        }
        preAuth.payslip2 ||= preAuth.payslips[1] ?? null;
        if (preAuth.payslip2 && preAuth.payslip2.payslipDate) {
          preAuth.payslip2.date = preAuth.payslip2.payslipDate;
          preAuth.payslip2.month = 2;
          this.income2 = +preAuth.payslip2.nettIncome + +preAuth.payslip2.otherIncome;
        }
        preAuth.payslip3 ||= preAuth.payslips[2] ?? null;
        if (preAuth.payslip3 && preAuth.payslip3.payslipDate) {
          preAuth.payslip3.date = preAuth.payslip3.payslipDate;
          preAuth.payslip3.month = 3;
          this.income3 = +preAuth.payslip3.nettIncome + +preAuth.payslip3.otherIncome;
        }
      }
    }
  }

  loadOrCalculateWeeklyIncomes(preAuth: PreAuthorisationModel) {
    // Check if we have weekly payslips stored
    if (preAuth.payslips && preAuth.payslips.length > 0) {
      // Filter weekly payslips (assuming they have a week property or we can identify them)
      const weeklyPays = preAuth.payslips.filter(p => p && p.payslipDate);
      if (weeklyPays.length >= 12) {
        // We have enough weekly payslips
        for (let i = 0; i < 12 && i < weeklyPays.length; i++) {
          this.weeklyIncomes[i] = +weeklyPays[i].nettIncome + +weeklyPays[i].otherIncome;
        }
      } else {
        // Convert monthly payslips to weekly
        this.convertToWeeklyIncomes(preAuth);
      }
    } else {
      // Convert monthly payslips to weekly
      this.convertToWeeklyIncomes(preAuth);
    }
  }

  loadOrCalculateFortnightlyIncomes(preAuth: PreAuthorisationModel) {
    // Check if we have fortnightly payslips stored
    if (preAuth.payslips && preAuth.payslips.length > 0) {
      // Filter fortnightly payslips
      const fortnightlyPays = preAuth.payslips.filter(p => p && p.payslipDate);
      if (fortnightlyPays.length >= 6) {
        // We have enough fortnightly payslips
        for (let i = 0; i < 6 && i < fortnightlyPays.length; i++) {
          this.fortnightlyIncomes[i] = +fortnightlyPays[i].nettIncome + +fortnightlyPays[i].otherIncome;
        }
      } else {
        // Convert monthly payslips to fortnightly
        this.convertToFortnightlyIncomes(preAuth);
      }
    } else {
      // Convert monthly payslips to fortnightly
      this.convertToFortnightlyIncomes(preAuth);
    }
  }

  convertToWeeklyIncomes(preAuth: PreAuthorisationModel) {
    // Simple conversion: distribute monthly incomes across weeks
    if (preAuth.payslip1) {
      const monthlyIncome = +preAuth.payslip1.nettIncome + +preAuth.payslip1.otherIncome;
      const weeklyIncome = monthlyIncome / 4;
      for (let i = 0; i < 4; i++) {
        this.weeklyIncomes[i] = weeklyIncome;
      }
    }
    if (preAuth.payslip2) {
      const monthlyIncome = +preAuth.payslip2.nettIncome + +preAuth.payslip2.otherIncome;
      const weeklyIncome = monthlyIncome / 4;
      for (let i = 4; i < 8; i++) {
        this.weeklyIncomes[i] = weeklyIncome;
      }
    }
    if (preAuth.payslip3) {
      const monthlyIncome = +preAuth.payslip3.nettIncome + +preAuth.payslip3.otherIncome;
      const weeklyIncome = monthlyIncome / 4;
      for (let i = 8; i < 12; i++) {
        this.weeklyIncomes[i] = weeklyIncome;
      }
    }
  }

  convertToFortnightlyIncomes(preAuth: PreAuthorisationModel) {
    // Simple conversion: distribute monthly incomes across fortnights
    if (preAuth.payslip1) {
      const monthlyIncome = +preAuth.payslip1.nettIncome + +preAuth.payslip1.otherIncome;
      const fortnightlyIncome = monthlyIncome / 2;
      this.fortnightlyIncomes[0] = fortnightlyIncome;
      this.fortnightlyIncomes[1] = fortnightlyIncome;
    }
    if (preAuth.payslip2) {
      const monthlyIncome = +preAuth.payslip2.nettIncome + +preAuth.payslip2.otherIncome;
      const fortnightlyIncome = monthlyIncome / 2;
      this.fortnightlyIncomes[2] = fortnightlyIncome;
      this.fortnightlyIncomes[3] = fortnightlyIncome;
    }
    if (preAuth.payslip3) {
      const monthlyIncome = +preAuth.payslip3.nettIncome + +preAuth.payslip3.otherIncome;
      const fortnightlyIncome = monthlyIncome / 2;
      this.fortnightlyIncomes[4] = fortnightlyIncome;
      this.fortnightlyIncomes[5] = fortnightlyIncome;
    }
  }

  public paydaySelect (payval?: any) {
    if (payval == null) {
      payval = this.payDate.value || this.employerDetailsModel?.payDate;
    }

    setTimeout(() => {
      this.f.payDate.setValue(payval, { onlySelf: true, emitEvent: true });
    }, 200);
  }

  fromAttribute (newVal) {
    return !newVal
      ? null
      : newVal.length === 0
      ? null
      : !newVal.includes(',')
      ? newVal
      : newVal.split(',').map((x) => x.trim());
  }

  toggleEmploymentType () {
    const employType = this.employmentType.value;

    if (employType == PERMANENTDESC) {
      this.employmentStartDate.enable();
      this.contractStartDate.disable();
      this.contractEndDate.disable();
      this.invalidEndDate = false;
    } else {
      this.employmentStartDate.disable();
      this.contractStartDate.enable();
      this.contractEndDate.enable();
    }

    const minemploymentStartDate = new Date();
    minemploymentStartDate.setMonth(minemploymentStartDate.getMonth() - 3);
    minemploymentStartDate.setDate(minemploymentStartDate.getDay() - 1);
    this.maxDate = minemploymentStartDate;

    this.applyEmploymentDateValidators();
  }

  private applyEmploymentDateValidators(): void {
    const isPermanentEmployment = this.employmentType.value === PERMANENTDESC;

    this.employmentStartDate.setValidators(isPermanentEmployment ? [Validators.required] : []);
    this.contractStartDate.setValidators(!isPermanentEmployment ? [Validators.required] : []);
    this.contractEndDate.setValidators(!isPermanentEmployment ? [Validators.required] : []);

    this.employmentStartDate.updateValueAndValidity({ onlySelf: true, emitEvent: false });
    this.contractStartDate.updateValueAndValidity({ onlySelf: true, emitEvent: false });
    this.contractEndDate.updateValueAndValidity({ onlySelf: true, emitEvent: false });
  }

  togglewageFrequency () {
    if (this.f.payDate.hasValidator(Validators.min(1))) {
      this.f.payDate.removeValidators(Validators.min(1));
    }
    this.f.payDate.setValue(null);

    this.f.payDate.addValidators(Validators.min(1));
    this.f.payDate.updateValueAndValidity();
    this.f.payDate.reset();

    // Clear income data when wage frequency changes
    this.income1 = this.income2 = this.income3 = 0;
    this.weeklyIncomes = [0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0];
    this.fortnightlyIncomes = [0, 0, 0, 0, 0, 0];
    this.weeklyPayslips = [];
    this.fortnightlyPayslips = [];

    console.log('Wage frequency changed to:', this.f.wageFrequency.value);
  }

  PayDateSelected (event: any) {
    const selectedDate = new Date(event.target.value);
    const payMonth = 3;
    const payYear = 0;
    this.payDateError = '';
    this.startDate = selectedDate;
    const employmentStartDate = new Date();

    employmentStartDate.setFullYear(employmentStartDate.getFullYear() - payYear);

    employmentStartDate.setMonth(employmentStartDate.getMonth() - payMonth);

    this.invalidDate = selectedDate > employmentStartDate;
    if (this.invalidDate) {
      this.payDateError = 'WARNING! start date is less than 3 months';
    }
  }

  PayEndDateSelected (event: any) {
    console.log(event.target.value);

    const selectedDate = new Date(event.target.value);
    const employmentStartDate = new Date(this.f.contractStartDate.value || this.f.employmentStartDate.value);

    this.invalidEndDate = selectedDate <= employmentStartDate;
    this.invalidDate = this.invalidEndDate;
    if (this.invalidEndDate) {
      this.payDateError = 'End Date must be after Start Date';
    }
  }

  goforNew () {
    this.branchEmployerService.preppedforCreate = true;

    const dialogRef = this.modalService.open(UpdateEmployerComponent, {
      ignoreBackdropClick: true,
      modalClass: 'modal-xl modal-dialog-centered modal-dialog-scrollable update-employer-modal',
      data: {
        employer: new EmployerDetailsModel(),
      },
    });

    dialogRef.onClose.subscribe((employer: any) => {
      if (employer) {
        this.doEmployerConfirm(employer);

        return true;
      }

      this.global_utility.warning('Employment details not saved', 'Incomplete employment');
      return false;
    });
  }

  doEmployerConfirm (event: any) {
    const incomingTnr = Number(event?.tnumber || 0);
    const incomingName = event?.name || event?.employerName || '';

    const employersSearch = {
      employerName: incomingName,
      name: incomingName,
      employerTNR: incomingTnr,
      branchSerial: this.global_utility.getGlobalData('branch_serial'),
    };

    const employerpromise = new Promise<boolean>((resolve) => {
      if ((!incomingTnr || incomingTnr == 0) && employersSearch.name === '') {
        resolve(false);
        return;
      }

      this.branchEmployerService.findEmployerBySerialTNR(employersSearch).subscribe({
        next: (employerList: any) => {
          this.employerDetailsModel = employerList;

          this.updateEmployment = false;
          if (this.originalEmployer == '' || this.originalEmployer != this.employerDetailsModel?.name) {
            this.updateEmployment = true;
          }

          const resolvedTnr = Number(
            employerList?.employerTNR ?? employerList?.tnumber ?? employerList?.id ?? incomingTnr ?? 0
          );
          const foundemployer = resolvedTnr > 0;
          resolve(foundemployer);
        },
        error: () => {
          resolve(false);
        },
      });
    });

    employerpromise.then((employerselected) => {
      if (employerselected) {
        const resolvedTnr = Number(
          event?.tnumber ?? this.employerDetailsModel?.employerTNR ?? this.employerDetailsModel?.id ?? 0
        );
        const employerDisplayName =
          incomingName ||
          this.employerDetailsModel?.name ||
          this.employerDetailsModel?.employerName ||
          this.branchEmployerService.employerDetailsModel?.employerName ||
          '';

        this.employerNametxt = employerDisplayName;

        const currentEmployerName = (this.employerNametxt ?? '').toString().trim().toLowerCase();
        if (currentEmployerName.startsWith('sassa')) {
          this.global_utility.error(
            'Client type is set to SALARIED, with employer as SASSA! This is not currently permitted!<br/><br/> Please select the correct client type/employment combination.',
            'Invalid employer'
          );
          this.f.employer.setErrors({ invalidEmployer: true},{ emitEvent: true })
          return;
        }

        // employerDetailsModel is populated after quicksearch result is selected
        this.f.employmentType.setValue(this.preauthModel!.employmentType || PERMANENTDESC, {
          onlySelf: false,
          emitEvent: true,
        });
        this.f.employer.setValue(employerDisplayName, { onlySelf: true, emitEvent: false });
        this.f.employerTNR.setValue(resolvedTnr, {
          onlySelf: true,
          emitEvent: true,
        });
        if (this.preauthModel!.employmentType === PERMANENTDESC) {
          this.f.employmentStartDate.setValue(this.preauthModel!.employment.employmentStartDate, {
            onlySelf: false,
            emitEvent: true,
          });

        } else {
          this.f.contractStartDate.setValue(this.preauthModel!.employment.contractStartDate, {
            onlySelf: false,
            emitEvent: true,
          });
          this.f.contractEndDate.setValue(this.preauthModel!.employment.contractEndDate, {
            onlySelf: false,
            emitEvent: true,
          });
        }
        this.f.wageFrequency.setValue(
          this.employerDetailsModel?.luEmpPayFrequency || this.preauthModel!.wageCalendar || '',
          { onlySelf: true, emitEvent: false }
        );
        this.f.industryType.setValue(this.preauthModel!.employment.industryType, {
          onlySelf: true,
          emitEvent: false,
        });
        this.f.industryTypeOther.setValue(this.preauthModel!.employment.industryTypeOther, {
          onlySelf: true,
          emitEvent: false,
        });
        this.f.payDate.setValue(this.employerDetailsModel!.payDate.toString() || '4', {
          onlySelf: true,
          emitEvent: true,
        });
        this.toggleEmploymentType();
      }
    })
    .catch((reason) => {
      console.error('Salaried employer error:', reason);
    });
  }

  openPaySlip (mnth: string | number) {
    console.log(`Saving payslip: ${mnth}`);
    let pslipModel = {} as PaySlipModel1;
    if (Number(mnth) === 1) {
      pslipModel = this.preauthModel!.payslip1;
    } else if (Number(mnth) === 2) {
      pslipModel = this.preauthModel!.payslip2;
    } else if (Number(mnth) === 3) {
      pslipModel = this.preauthModel!.payslip3;
    }

    this.lmsDialogHandler.openPayslipDialog(mnth, pslipModel, this.sallabel)
      ?.onClose.subscribe((pslip: PaySlipModel1) => {
        if (pslip) {
          pslip.otherIncome ||= 0;

          const totIn = +pslip.nettIncome + +pslip.otherIncome;
          this.payslipResult = JSON.stringify(pslip);
          console.log(this.payslipResult, 'payslipR');

          if (mnth == '1') {
            this.preauthModel!.payslip1 = pslip;
            this.payslipControls!.payslip1?.patchValue(pslip, {
              onlySelf: true,
              emitEvent: true,
            });
            this.income1 = totIn;
          } else if (mnth == '2') {
            this.preauthModel!.payslip2 = pslip;
            this.payslipControls?.payslip2!.patchValue(pslip, {
              onlySelf: true,
              emitEvent: true,
            });
            this.income2 = totIn;
          } else if (mnth == '3') {
            this.preauthModel!.payslip3 = pslip;
            this.payslipControls!.payslip3.patchValue(pslip, {
              onlySelf: true,
              emitEvent: true,
            });
            this.income3 = totIn;
          }
          this.payslips.updateValueAndValidity();

          this.paySlipCaptured.emit(pslip);
        }
      });
  }

  // Methods for Weekly income capture
  openWeeklyPaySlip(monthIndex: number, weekIndex: number) {
    console.log(`Opening weekly payslip for Month ${monthIndex}, Week ${weekIndex}`);

    // Calculate array index (Month 1: 0-3, Month 2: 4-7, Month 3: 8-11)
    const arrayIndex = ((monthIndex - 1) * 4) + (weekIndex - 1);

    // Check if we already have a payslip for this week
    const existingPayslip = this.weeklyPayslips.find(p =>
      p.month === monthIndex && (p as any).week === weekIndex
    );

    const pslipModel = existingPayslip || {} as PaySlipModel1;
    pslipModel.month = monthIndex;

    // Add custom week property to track weekly income
    (pslipModel as any).week = weekIndex;

    this.lmsDialogHandler.openPayslipDialog(`${monthIndex}-${weekIndex}`, pslipModel, 'Weekly')
      ?.onClose.subscribe((pslip: PaySlipModel1) => {
        if (pslip) {
          pslip.otherIncome ||= 0;
          const totIn = +pslip.nettIncome + +pslip.otherIncome;

          // Update weekly income
          this.weeklyIncomes[arrayIndex] = totIn;

          // Store or update the payslip
          const existingIndex = this.weeklyPayslips.findIndex(p =>
            p.month === monthIndex && (p as any).week === weekIndex
          );

          if (existingIndex >= 0) {
            this.weeklyPayslips[existingIndex] = pslip;
          } else {
            this.weeklyPayslips.push(pslip);
          }

          console.log(`Updated weekly income at index ${arrayIndex}: ${totIn}`);

          // Update the main payslips array if needed
          this.updatePayslipsArray();
        }
      });
  }

  // Methods for Fortnightly income capture - FIXED with correct value
  openFortnightlyPaySlip(monthIndex: number, fortnightIndex: number) {
    console.log(`Opening fortnightly payslip for Month ${monthIndex}, Fortnight ${fortnightIndex}`);

    // Calculate array index (Month 1: 0-1, Month 2: 2-3, Month 3: 4-5)
    const arrayIndex = ((monthIndex - 1) * 2) + (fortnightIndex - 1);

    // Check if we already have a payslip for this fortnight
    const existingPayslip = this.fortnightlyPayslips.find(p =>
      p.month === monthIndex && (p as any).fortnight === fortnightIndex
    );

    const pslipModel = existingPayslip || {} as PaySlipModel1;
    pslipModel.month = monthIndex;

    // Add custom fortnight property to track fortnightly income
    (pslipModel as any).fortnight = fortnightIndex;

    this.lmsDialogHandler.openPayslipDialog(`${monthIndex}-F${fortnightIndex}`, pslipModel, 'Fortnightly')
      ?.onClose.subscribe((pslip: PaySlipModel1) => {
        if (pslip) {
          pslip.otherIncome ||= 0;
          const totIn = +pslip.nettIncome + +pslip.otherIncome;

          // Update fortnightly income
          this.fortnightlyIncomes[arrayIndex] = totIn;

          // Store or update the payslip
          const existingIndex = this.fortnightlyPayslips.findIndex(p =>
            p.month === monthIndex && (p as any).fortnight === fortnightIndex
          );

          if (existingIndex >= 0) {
            this.fortnightlyPayslips[existingIndex] = pslip;
          } else {
            this.fortnightlyPayslips.push(pslip);
          }

          console.log(`Updated fortnightly income at index ${arrayIndex}: ${totIn}`);

          // Update the main payslips array if needed
          this.updatePayslipsArray();
        }
      });
  }

  updatePayslipsArray() {
    if (this.preauthModel) {
      const wageFreq = this.f.wageFrequency.value;

      if (wageFreq === 'Weekly') {
        this.preauthModel.payslip1 = {
          ...this.preauthModel.payslip1,
          nettIncome: this.getWeeklyMonthTotal(1),
          grossIncome: this.getWeeklyMonthTotal(1),
          month: 1
        };
        this.preauthModel.payslip2 = {
          ...this.preauthModel.payslip2,
          nettIncome: this.getWeeklyMonthTotal(2),
          grossIncome: this.getWeeklyMonthTotal(2),
          month: 2
        };
        this.preauthModel.payslip3 = {
          ...this.preauthModel.payslip3,
          nettIncome: this.getWeeklyMonthTotal(3),
          grossIncome: this.getWeeklyMonthTotal(3),
          month: 3
        };
      } else if (wageFreq === 'Fortnightly') {
        this.preauthModel.payslip1 = {
          ...this.preauthModel.payslip1,
          nettIncome: this.getFortnightlyMonthTotal(1),
          grossIncome: this.getFortnightlyMonthTotal(1),
          month: 1
        };
        this.preauthModel.payslip2 = {
          ...this.preauthModel.payslip2,
          nettIncome: this.getFortnightlyMonthTotal(2),
          grossIncome: this.getFortnightlyMonthTotal(2),
          month: 2
        };
        this.preauthModel.payslip3 = {
          ...this.preauthModel.payslip3,
          nettIncome: this.getFortnightlyMonthTotal(3),
          grossIncome: this.getFortnightlyMonthTotal(3),
          month: 3
        };
      }

      const allPayslips = [
        ...this.weeklyPayslips,
        ...this.fortnightlyPayslips,
        this.preauthModel.payslip1,
        this.preauthModel.payslip2,
        this.preauthModel.payslip3
      ].filter(p => p && p.nettIncome !== undefined);

      this.preauthModel.payslips = allPayslips;
    }
  }

  // Helper methods for validation
  hasEmptyWeeklyIncomes(): boolean {
    return this.weeklyIncomes.some(inc => inc === 0);
  }

  hasEmptyFortnightlyIncomes(): boolean {
    return this.fortnightlyIncomes.some(inc => inc === 0);
  }

  hasEmptyMonthlyIncomes(): boolean {
    return this.income1 === 0 || this.income2 === 0 || this.income3 === 0;
  }

  shouldShowValidationError(): boolean {
    if (!this.Submitted) return false;

    if (this.isMonthlyClient) {
      return this.hasEmptyMonthlyIncomes();
    } else if (this.isWeeklyClient) {
      return this.hasEmptyWeeklyIncomes();
    } else if (this.isFortnightlyClient) {
      return this.hasEmptyFortnightlyIncomes();
    }

    return false;
  }

  updateAdditionalIncomeOptions (value) {
    const selectedValue = `${value ?? ''}`.trim().toLowerCase();
    this.isAINoneSelected = selectedValue.includes('none');
    console.log('isMONoneSelected:', this.isAINoneSelected);
    this.isAIOtherOptionSelected = selectedValue.includes('other');

    if (this.isAINoneSelected) {
      this.preauthModel!.selectedadditionalIncome = 'None';
      this.isAIOtherOptionSelected = false;
      return;
    }
    console.log('isAIOtherSelected:', this.isAIOtherOptionSelected);
  }

  onAdditionalIncomeSelectionChange(value: any): void {
    const selectedValue = `${value?.value ?? value ?? ''}`.trim();
    this.updateAdditionalIncomeOptions(selectedValue);
  }

  ngOnDestroy () {
    this.additionalIncomeSub?.unsubscribe();
    this.employmentTypeSub?.unsubscribe();
    this.parentFormGroup.removeControl(this.controlKey);
  }
}
